import React from 'react';
import WikiIcon from '../../shared/WikiIcon';
import { WikiError, WikiLoading } from '../../shared/WikiStates';

/**
 * One topic's investigation view (E2 §2.1 item 3), replacing the topic table:
 * "All topics" back + name + meta, the amber audit notice with Show/Hide names,
 * "What the Brain noticed:", then the conversation list and the transcript
 * panel (person bubbles dark/right with the Heart's read, AI bubbles teal/left
 * with how it answered). Names are pseudonyms ("Person 1 · Account
 * executive") until revealed.
 *
 * `drill` = { topic (table row), status: 'loading'|'ready'|'error', data
 * (GET payload), revealed (reveal payload | null), revealing, error, convId }.
 */

const TONES = { ok: 'wk-pill--green', bad: 'wk-pill--red', warn: 'wk-pill--orange' };

function noticeText(days) {
    const n = Number(days) > 0 ? Number(days) : 30;
    return `Opening a topic's conversations is recorded in the audit log. Names stay hidden unless you show them, and per-message reads are deleted after ${n} ${n === 1 ? 'day' : 'days'}.`;
}

function topicMeta(topic, row) {
    if (topic?.meta) return topic.meta;
    const q = topic?.questions ?? row?.questions ?? 0;
    const cat = topic?.category_name ?? row?.category_name;
    return cat ? `${q} questions this week · ${cat}` : `${q} questions this week`;
}

function Message({ message }) {
    const isAi = message.speaker === 'ai';
    const prefix = isAi ? 'How it answered: ' : 'What the Brain read: ';
    return (
        <div className={`wk-bri-msg wk-bri-msg--${isAi ? 'ai' : 'person'}`} data-testid="brain-conv-message" data-speaker={isAi ? 'ai' : 'person'}>
            <div className={`wk-bri-bubble wk-bri-bubble--${isAi ? 'ai' : 'person'}`}>{message.text}</div>
            {message.meta && (
                <div className={`wk-bri-msg-meta wk-bri-msg-meta--${isAi ? 'ai' : 'person'}`}>
                    <WikiIcon name={isAi ? 'sliders-horizontal' : 'heart-pulse'} size={12} />
                    <span>{prefix}{message.meta}</span>
                </div>
            )}
        </div>
    );
}

export default function TopicDrilldown({ drill, retentionDays, onBack, onRetry, onToggleReveal, onSelectConversation }) {
    const base = drill.data || {};
    const shown = drill.revealed || base;
    const topic = shown.topic || base.topic || null;
    const row = drill.topic || {};
    const conversations = Array.isArray(shown.conversations) ? shown.conversations : [];
    const selected = conversations.find((c) => c.id === drill.convId) || conversations[0] || null;
    const revealed = Boolean(drill.revealed);
    const days = base.retention_days ?? retentionDays;
    const ready = drill.status === 'ready';
    const signal = topic?.signal ?? row.signal;

    return (
        <section className="wk-bri-drill" aria-label={`${topic?.name || row.name} conversations`} data-testid="brain-topic-drilldown">
            <div className="wk-bri-drill-head">
                <button type="button" className="wk-bri-back" onClick={onBack} data-testid="brain-topic-back">
                    <WikiIcon name="arrow-left" size={13} />
                    All topics
                </button>
                <h3 className="wk-t-h">{topic?.name || row.name}</h3>
                <span className="wk-t-meta" data-testid="brain-topic-meta">{topicMeta(topic, row)}</span>
            </div>

            <div className="wk-bri-notice" role="note" data-testid="brain-audit-notice">
                <WikiIcon name="eye" size={16} />
                <span className="wk-bri-notice-text">
                    <b>Investigation view.</b> {noticeText(days)}
                </span>
                <button
                    type="button"
                    className="wk-bri-reveal"
                    onClick={onToggleReveal}
                    disabled={!ready || drill.revealing}
                    aria-pressed={revealed}
                    data-testid="brain-reveal-toggle"
                >
                    <WikiIcon name="eye-off" size={13} />
                    {revealed ? 'Hide names' : 'Show names'}
                </button>
            </div>

            {signal && (
                <div className="wk-bri-noticed" data-testid="brain-topic-noticed">
                    <b>What the Brain noticed:</b> {signal}
                </div>
            )}

            {drill.status === 'loading' && (
                <div className="wk-card"><WikiLoading label="Opening conversations…" /></div>
            )}
            {drill.status === 'error' && (
                <div className="wk-card" data-testid="brain-drill-error">
                    <WikiError error={drill.error} onRetry={onRetry} title="Conversations stay hidden." />
                </div>
            )}
            {ready && conversations.length === 0 && (
                <div className="wk-bro-empty" data-testid="brain-convs-empty">
                    No conversations about this topic in the last {Number(days) > 0 ? Number(days) : 30} days.
                </div>
            )}
            {ready && conversations.length > 0 && (
                <div className="wk-bri-panes">
                    <div className="wk-bri-convs" role="group" aria-label="Conversations">
                        {conversations.map((conv) => {
                            const isSelected = selected && selected.id === conv.id;
                            return (
                                <button
                                    key={conv.id}
                                    type="button"
                                    className={`wk-bri-conv${isSelected ? ' is-selected' : ''}`}
                                    aria-pressed={Boolean(isSelected)}
                                    onClick={() => onSelectConversation(conv.id)}
                                    data-testid="brain-conv-row"
                                    data-conv-id={conv.id}
                                >
                                    <span className="wk-bri-conv-top">
                                        <span className="wk-bri-conv-who" data-testid="brain-conv-who">{conv.who}</span>
                                        <span className="wk-t-meta">{conv.when}</span>
                                    </span>
                                    <span className="wk-bri-conv-q">&quot;{conv.question}&quot;</span>
                                    <span>
                                        <span className={`wk-pill ${TONES[conv.tone] || TONES.warn}`} data-testid="brain-conv-outcome">{conv.outcome}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {selected && (
                        <div className="wk-bri-panel" data-testid="brain-conv-panel">
                            <div className="wk-t-name wk-bri-panel-title" data-testid="brain-conv-title">
                                {selected.title || `${revealed && selected.name ? selected.name : 'Conversation'} · ${selected.when}`}
                            </div>
                            <div className="wk-bri-msgs">
                                {(selected.messages || []).map((message, index) => (
                                    <Message key={`${message.speaker}-${index}`} message={message} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
